/**
 * useSettings - CRUD hook for user/org/platform settings
 * Provides typed access to settings with optimistic updates
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

export type SettingsCategory = 
  | 'general'
  | 'notifications'
  | 'display'
  | 'security'
  | 'voice'
  | 'mail'
  | 'crm'
  | 'agent_workspace'
  | 'integrations';

export type SettingsScope = 'user' | 'org' | 'platform';

export interface SettingRecord {
  id: string;
  category: SettingsCategory;
  key: string;
  value: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface UserSetting extends SettingRecord {
  user_id: string;
}

export interface OrgSetting extends SettingRecord {
  org_id: string;
  updated_by: string | null;
}

export interface PlatformSetting extends SettingRecord {
  description: string | null;
  updated_by: string | null;
}

// ========== USER SETTINGS ==========

export function useUserSettings(category?: SettingsCategory) {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['user-settings', user?.id, category],
    queryFn: async () => {
      if (!user?.id) return [];
      
      let query = supabase
        .from('user_settings')
        .select('*')
        .eq('user_id', user.id);
      
      if (category) {
        query = query.eq('category', category);
      }
      
      const { data, error } = await query.order('key');
      if (error) throw error;
      return data as UserSetting[];
    },
    enabled: !!user?.id,
  });
}

export function useUserSetting(category: SettingsCategory, key: string) {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: ['user-setting', user?.id, category, key],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data, error } = await supabase
        .from('user_settings')
        .select('*')
        .eq('user_id', user.id)
        .eq('category', category)
        .eq('key', key)
        .maybeSingle();
      
      if (error) throw error;
      return data as UserSetting | null;
    },
    enabled: !!user?.id,
  });
}

export function useUpsertUserSetting() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  return useMutation({
    mutationFn: async ({ 
      category, 
      key, 
      value 
    }: { 
      category: SettingsCategory; 
      key: string; 
      value: Record<string, unknown>;
    }) => {
      if (!user?.id) throw new Error('Not authenticated');
      
      const { data, error } = await supabase
        .from('user_settings')
        .upsert({
          user_id: user.id,
          category,
          key,
          value,
        } as never, { 
          onConflict: 'user_id,category,key' 
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['user-settings'] });
      queryClient.invalidateQueries({ 
        queryKey: ['user-setting', user?.id, variables.category, variables.key] 
      });
      toast.success('Settings saved');
    },
    onError: (error) => {
      console.error('Settings save error:', error);
      toast.error('Failed to save settings');
    },
  });
}

export function useDeleteUserSetting() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  return useMutation({
    mutationFn: async ({ category, key }: { category: SettingsCategory; key: string }) => {
      if (!user?.id) throw new Error('Not authenticated');
      
      const { error } = await supabase
        .from('user_settings')
        .delete()
        .eq('user_id', user.id)
        .eq('category', category)
        .eq('key', key);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-settings'] });
      toast.success('Setting deleted');
    },
    onError: () => {
      toast.error('Failed to delete setting');
    },
  });
}

// ========== ORG SETTINGS ==========

export function useOrgSettings(orgId: string | undefined, category?: SettingsCategory) {
  return useQuery({
    queryKey: ['org-settings', orgId, category],
    queryFn: async () => {
      if (!orgId) return [];
      
      let query = supabase
        .from('org_settings')
        .select('*')
        .eq('org_id', orgId);
      
      if (category) {
        query = query.eq('category', category);
      }
      
      const { data, error } = await query.order('key');
      if (error) throw error;
      return data as OrgSetting[];
    },
    enabled: !!orgId,
  });
}

export function useOrgSetting(orgId: string | undefined, category: SettingsCategory, key: string) {
  return useQuery({
    queryKey: ['org-setting', orgId, category, key],
    queryFn: async () => {
      if (!orgId) return null;
      
      const { data, error } = await supabase
        .from('org_settings')
        .select('*')
        .eq('org_id', orgId)
        .eq('category', category)
        .eq('key', key)
        .maybeSingle();
      
      if (error) throw error;
      return data as OrgSetting | null;
    },
    enabled: !!orgId,
  });
}

export function useUpsertOrgSetting() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  return useMutation({
    mutationFn: async ({ 
      orgId,
      category, 
      key, 
      value 
    }: { 
      orgId: string;
      category: SettingsCategory; 
      key: string; 
      value: Record<string, unknown>;
    }) => {
      const { data, error } = await supabase
        .from('org_settings')
        .upsert({
          org_id: orgId,
          category,
          key,
          value,
          updated_by: user?.id,
        } as never, { 
          onConflict: 'org_id,category,key' 
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['org-settings', variables.orgId] });
      queryClient.invalidateQueries({ 
        queryKey: ['org-setting', variables.orgId, variables.category, variables.key] 
      });
      toast.success('Organization settings saved');
    },
    onError: (error) => {
      console.error('Org settings save error:', error);
      toast.error('Failed to save settings');
    },
  });
}

// ========== PLATFORM SETTINGS (Admin only) ==========

export function usePlatformSettings(category?: SettingsCategory) {
  return useQuery({
    queryKey: ['platform-settings', category],
    queryFn: async () => {
      let query = supabase.from('platform_settings').select('*');
      
      if (category) {
        query = query.eq('category', category);
      }
      
      const { data, error } = await query.order('key');
      if (error) throw error;
      return data as PlatformSetting[];
    },
  });
}

export function usePlatformSetting(key: string) {
  return useQuery({
    queryKey: ['platform-setting', key],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('platform_settings')
        .select('*')
        .eq('key', key)
        .maybeSingle();
      
      if (error) throw error;
      return data as PlatformSetting | null;
    },
  });
}

export function useUpsertPlatformSetting() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  return useMutation({
    mutationFn: async ({ 
      category, 
      key, 
      value,
      description 
    }: { 
      category: SettingsCategory; 
      key: string; 
      value: Record<string, unknown>;
      description?: string;
    }) => {
      const { data, error } = await supabase
        .from('platform_settings')
        .upsert({
          category,
          key,
          value,
          description,
          updated_by: user?.id,
        } as never, { 
          onConflict: 'key' 
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['platform-settings'] });
      queryClient.invalidateQueries({ queryKey: ['platform-setting', variables.key] });
      toast.success('Platform settings saved');
    },
    onError: (error) => {
      console.error('Platform settings save error:', error);
      toast.error('Failed to save settings');
    },
  });
}

// ========== CONVENIENCE HOOKS ==========

/**
 * Get a single setting value with default
 */
export function useSettingValue<T = unknown>(
  scope: 'user' | 'org' | 'platform',
  category: SettingsCategory,
  key: string,
  defaultValue: T,
  orgId?: string
): { value: T; isLoading: boolean } {
  const { user } = useAuth();
  
  const userQuery = useQuery({
    queryKey: ['setting-value', 'user', user?.id, category, key],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase
        .from('user_settings')
        .select('value')
        .eq('user_id', user.id)
        .eq('category', category)
        .eq('key', key)
        .maybeSingle();
      return data?.value as T | null;
    },
    enabled: scope === 'user' && !!user?.id,
  });
  
  const orgQuery = useQuery({
    queryKey: ['setting-value', 'org', orgId, category, key],
    queryFn: async () => {
      if (!orgId) return null;
      const { data } = await supabase
        .from('org_settings')
        .select('value')
        .eq('org_id', orgId)
        .eq('category', category)
        .eq('key', key)
        .maybeSingle();
      return data?.value as T | null;
    },
    enabled: scope === 'org' && !!orgId,
  });
  
  const platformQuery = useQuery({
    queryKey: ['setting-value', 'platform', category, key],
    queryFn: async () => {
      const { data } = await supabase
        .from('platform_settings')
        .select('value')
        .eq('key', key)
        .maybeSingle();
      return data?.value as T | null;
    },
    enabled: scope === 'platform',
  });
  
  if (scope === 'user') {
    return { 
      value: (userQuery.data ?? defaultValue) as T, 
      isLoading: userQuery.isLoading 
    };
  }
  if (scope === 'org') {
    return { 
      value: (orgQuery.data ?? defaultValue) as T, 
      isLoading: orgQuery.isLoading 
    };
  }
  return { 
    value: (platformQuery.data ?? defaultValue) as T, 
    isLoading: platformQuery.isLoading 
  };
}
